/* this might become a server component in the future. It depends on ComponentRecognizer */
'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackMarketing/useTrackMarketing';
import { useRef } from 'react';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import { classes } from '../../utils/classes';
import { OverlayCircleItem } from './OverlayCircleItem.client';
import type { OverlayCircleType } from './types';

/**
 * This component displays round overlays in different layout variants on static pages.
 *
 * @param data Object contains data
 * @returns JSX.Element to be placed in PageLayout
 */
export const OverlayCircle = ({ data }: { data: OverlayCircleType }) => {
  const {
    behavior_paragraph_overlay_scale: scalingFactor = '1',
    behavior_paragraph_overlay_layout: layout = 'right',
    behavior_paragraph_background_color: styleBackgroundColor,
    behavior_paragraph_link_target: linkTarget,
    behavior_paragraph_link_rel: linkRelRaw,
    field_link: link,
    field_tracking: linkTracking,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkFilter,
    field_bonus_number: linkBonusNumber,
  } = data;
  const linkRel = linkRelRaw === '_none' ? undefined : linkRelRaw;

  const tmpLink = buildLinkWithArguments(link, {
    field_tracking: linkTracking,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkFilter,
    field_bonus_number: linkBonusNumber,
    field_link_target: linkTarget,
  });

  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);
  const teaserRef = useRef<HTMLAnchorElement>(null);
  const { trackClick } = useTrackMarketing(data, teaserRef, finalLink);

  const classNames = classes(
    'c_cms_OC absolute box-border flex flex-col justify-center items-center p-2 overflow-hidden text-center rounded-full',
    !styleBackgroundColor && 'bg-color-primary-dark',
  );

  if (link) {
    return (
      <Link
        rel={linkRel}
        ref={teaserRef}
        href={finalLink}
        className={classes(
          classNames,
          'pointer-events-auto' /** GroupOnImage sets pointer-events-none to allow the image underneath to be the link. However, we need to be able to click on the linked circle */,
        )}
        onClick={() => trackClick()}
        style={{ background: styleBackgroundColor }}
        data-position={layout}
        data-scaling={scalingFactor}
      >
        <OverlayCircleItem data={data} />
      </Link>
    );
  }

  return (
    <span
      className={classes(classNames, 'pointer-events-none')}
      style={{ background: styleBackgroundColor }}
      data-position={layout}
      data-scaling={scalingFactor}
    >
      <OverlayCircleItem data={data} />
    </span>
  );
};
